import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Commands, People, Search } from '@local/client-contracts';
import { PopupRef, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { KeyboardService } from '@shared/services/keyboard.service';
import { getModifiers, isEnterKey } from '@local/ts-infra';
import { Tooltip } from 'primeng/tooltip';
import { ComponentFocused, FocusDirection } from 'src/app/bar/models/component-focused.model';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { actionToIcon } from 'src/app/bar/views/results';
import { PreviewMode } from '../../../model/preview-mode';

@UntilDestroy()
@Component({
  selector: 'preview-details',
  templateUrl: './preview-details.component.html',
  styleUrls: ['./preview-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewDetailsComponent implements OnDestroy, AfterViewInit {
  private activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  private readonly COMP_INDEX = 2;
  private keyHandlerId: string;
  private maxMarkedIndex = -1;
  markedIndex: number;
  tooltipText = 'Click to copy';
  @Input() details: People.PersonDetails;
  @Input() searchId: string;
  @Input() set direction(val: FocusDirection) {
    if (!val) return;
    this.markedIndex = val === 'first' ? 0 : this.maxMarkedIndex;
  }
  @Input() size: PreviewMode;
  @Input() personIndex: number;
  @Input() model: Search.ResultResourceItem;
  @Input() set componentFocused({ compIndex, direction }: ComponentFocused) {
    if (compIndex === this.COMP_INDEX) {
      if (this.keyHandlerId) return;
      this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
        this.handleKeys(keys, event);
      }, 6);
      return;
    }
    this.unregisterKeyHandler();
  }
  @Input() smallWidthPreview = true;
  @Input() previewPopupLarge: boolean;
  @Output() componentFocusedExit: EventEmitter<string> = new EventEmitter<string>();
  @Output() readyForFocus: EventEmitter<number> = new EventEmitter<number>();
  @ViewChildren(Tooltip) private tooltips: QueryList<Tooltip>;
  readonly detailsView: string[] = ['email', 'phone', 'department', 'location'];
  private get eventResources() {
    return [
      {
        id: this.model?.id,
        list: 'people_view',
        appId: this.model?.link?.appId,
        type: this.model?.resource?.type,
        position: this.personIndex,
        linkId: this.model?.link?.id,
      },
    ];
  }

  constructor(
    private resultCommandService: ResultCommandService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
    private keyboardService: KeyboardService,
    private actionsRef: ElementRef,
    private hubService: HubService
  ) {}
  ngAfterViewInit(): void {
    if (this.details.email) this.maxMarkedIndex++;
    if (this.details?.phone) this.maxMarkedIndex++;
    if (this.maxMarkedIndex >= 0) {
      this.readyForFocus.emit(this.COMP_INDEX);
    }
  }

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
  }

  private async buildContextMenuItems(valueToCopy: string, type: string): Promise<ContextMenuItem[]> {
    const items: ContextMenuItem[] = [];
    if (type === 'email') {
      items.push({
        id: 'copy_email',
        text: 'Copy Email Address',
        command: { type: 'copy-clipboard', value: valueToCopy } as Commands.CopyClipboard,
        icon: { type: 'font-icon', value: actionToIcon['copy-clipboard'] },
      });
    } else {
      items.push({
        id: 'copy_phone',
        text: 'Copy Phone Number',
        command: { type: 'copy-clipboard', value: this.buildPhoneNumber(valueToCopy) } as Commands.CopyClipboard,
        icon: { type: 'font-icon', value: actionToIcon['copyPhone'] },
      });
    }
    return items;
  }

  async copyEmailAndPhone(event: MouseEvent, valueToCopy: any, type: string) {
    let openAt: { x: number; y: number } = event;
    if (!openAt.x && !openAt.y) {
      const { width, left, top, height } = this.actionsRef.nativeElement.getBoundingClientRect();
      if (width && height) {
        openAt = { x: left + width / 2, y: top / 2 + height };
      }
    }
    const items = await this.buildContextMenuItems(valueToCopy, type);
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) =>
          this.resultCommandService.executeCommand(item.command, { item: this.model, searchId: this.searchId }, 'result', {
            location: { title: this.hubService.currentLocation },
            label: type === 'email' ? 'copy_email' : 'copy_number',
            target: 'mouse_click',
            resources: this.eventResources,
          }),
      },
      { position: 'below' }
    );
    this.cdr.markForCheck();
    this.activeContextMenu.close$.pipe(untilDestroyed(this)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = null;
    this.markedIndex = -1;
  }

  generateIcon(iconName: string): UiIconModel {
    switch (iconName) {
      case 'phone':
        iconName = 'iphone';
        break;
      case 'email':
        iconName = 'envelope';
        break;
      default:
        break;
    }
    const fontIcon: UiIconModel = {
      type: 'font',
      value: `icon-${iconName}`,
    };
    return fontIcon;
  }

  generateText(item: string) {
    if (item === 'phone') {
      return this.buildPhoneNumber(this.details.phone);
    }
    return this.details[item];
  }

  private buildPhoneNumber(phone): string {
    return phone?.code ? `+${phone.code}-${phone.number}` : phone.number;
  }

  handleKeys(keys, event) {
    const modifiers = getModifiers(keys);
    const key = keys[0];
    if (key === 'tab') {
      event.stopPropagation();
      event.preventDefault();
      if (modifiers[0] === 'shift') {
        if (this.markedIndex === 0) {
          this.componentFocusedExit.emit('prev');
        }
        this.markedIndex--;
        this.cdr.markForCheck();
        return;
      }
      if (this.markedIndex === this.maxMarkedIndex) {
        this.componentFocusedExit.emit('next');
      } else {
        this.markedIndex++;
      }
    }
    if (isEnterKey(key)) {
      const eventValue = this.detailsView[this.markedIndex];
      this.copyEmailAndPhone(event, this.details[eventValue], eventValue);
      event.stopPropagation();
    }
    this.cdr.markForCheck();
  }
}
